/**
 * DEX UI Main Entry Point
 * Initializes the application and sets up state subscriptions.
 */

import './style.css';

import { subscribe, getState } from './state/store.js';
import { initSession, setAuthenticated, clearSession } from './state/session.js';
import { initLoginForm } from './ui/components/login-form.js';
import { authenticateKey } from './api/auth.js';
import { initTimeoutWarning } from './ui/components/timeout-warning.js';
import { startIdleTimer, stopIdleTimer } from './utils/idle-timer.js';
import { initWriterForm } from './ui/components/writer-form.js';
import { renderReaderView, initReaderView } from './ui/components/reader-view.js';
import { renderAdminDashboard, initAdminDashboard } from './ui/components/admin-dashboard.js';
import { renderSuperAdminDashboard, initSuperAdminDashboard } from './ui/components/super-admin-dashboard.js';
import { clearAdminState, setActiveTab, getPersistedActiveTab } from './state/admin.js';
import { initMacroControls } from './ui/components/macro-controls.js';
import { initMacroPreview } from './ui/components/macro-preview.js';

// Track cleanup functions
const cleanupFunctions = [];

// Track writer form cleanup separately
let cleanupWriterForm = null;

// Track reader view cleanup separately
let cleanupReaderView = null;

// Track admin dashboard cleanup separately
let cleanupAdminDashboard = null;

// Track super-admin dashboard cleanup separately
let cleanupSuperAdminDashboard = null;

// Track macro controls cleanup separately
let cleanupMacroControls = null;

// Track macro preview cleanup separately
let cleanupMacroPreview = null;

// Track previous auth state for timer management
let wasAuthenticated = false;

/**
 * Clean up all role-specific views and their state.
 */
function cleanupAllRoleViews() {
  if (cleanupWriterForm) {
    cleanupWriterForm();
    cleanupWriterForm = null;
  }
  if (cleanupReaderView) {
    cleanupReaderView();
    cleanupReaderView = null;
  }
  if (cleanupAdminDashboard) {
    cleanupAdminDashboard();
    cleanupAdminDashboard = null;
  }
  if (cleanupSuperAdminDashboard) {
    cleanupSuperAdminDashboard();
    cleanupSuperAdminDashboard = null;
  }
}

/**
 * Hide all role-specific containers.
 */
function hideAllRoleContainers() {
  const dashboardContent = document.getElementById('dashboard-content');
  const adminContainer = document.getElementById('admin-dashboard-container');
  const superAdminContainer = document.getElementById('super-admin-dashboard-container');

  dashboardContent.innerHTML = '';
  dashboardContent.classList.add('hidden');

  if (adminContainer) {
    adminContainer.classList.add('hidden');
    adminContainer.innerHTML = '';
  }
  if (superAdminContainer) {
    superAdminContainer.classList.add('hidden');
    superAdminContainer.innerHTML = '';
  }
}

/**
 * Render the main dashboard content based on authentication state.
 * Routes to role-specific views based on authenticated role.
 */
function renderDashboard() {
  const dashboardContent = document.getElementById('dashboard-content');
  const adminContainer = document.getElementById('admin-dashboard-container');
  const superAdminContainer = document.getElementById('super-admin-dashboard-container');
  const state = getState();
  const { session } = state;

  // Clean up previous role views (cleanup functions also clear their state)
  cleanupAllRoleViews();

  if (!session.authenticated) {
    // Hide admin containers, show dashboard content
    hideAllRoleContainers();
    dashboardContent.classList.remove('hidden');
    dashboardContent.innerHTML = `
      <div class="text-center py-16">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
          <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path>
          </svg>
        </div>
        <h2 class="text-xl font-semibold text-gray-700 mb-2">Welcome to DEX UI</h2>
        <p class="text-gray-500 mb-4">Enter your API key to get started</p>
        <p class="text-sm text-gray-400">
          Click "Login" in the header to authenticate
        </p>
      </div>
    `;

    // Reset header border
    const header = document.getElementById('app-header');
    header.className = header.className.replace(/role-\w+/g, '');

    return;
  }

  // Authenticated - show role-specific view
  // Hide all containers first
  hideAllRoleContainers();

  switch (session.role) {
    case 'writer':
      // Writer role gets the writer form
      dashboardContent.classList.remove('hidden');
      dashboardContent.innerHTML = `
        <div class="max-w-2xl mx-auto py-6">
          <div id="writer-form-container"></div>
        </div>
      `;
      const formContainer = document.getElementById('writer-form-container');
      cleanupWriterForm = initWriterForm(formContainer);
      break;

    case 'reader':
      // Reader role gets the reader view
      dashboardContent.classList.remove('hidden');
      dashboardContent.innerHTML = `
        <div class="max-w-2xl mx-auto py-6">
          <div id="reader-view-container"></div>
        </div>
      `;
      const viewContainer = document.getElementById('reader-view-container');
      renderReaderView(viewContainer);
      cleanupReaderView = initReaderView(viewContainer);
      break;

    case 'admin':
      // Admin role gets the admin dashboard
      if (adminContainer) {
        adminContainer.classList.remove('hidden');
        renderAdminDashboard(adminContainer, state);
        cleanupAdminDashboard = initAdminDashboard(adminContainer);
      }
      break;

    case 'super_admin':
      // Super-admin role gets the super-admin dashboard
      if (superAdminContainer) {
        // Restore persisted tab before first render
        const persistedTab = getPersistedActiveTab();
        if (persistedTab !== state.admin?.activeTab) {
          setActiveTab(persistedTab);
        }
        superAdminContainer.classList.remove('hidden');
        // Use fresh state after potential tab update
        renderSuperAdminDashboard(superAdminContainer, getState());
        cleanupSuperAdminDashboard = initSuperAdminDashboard(superAdminContainer);
      }
      break;

    default:
      // Unknown role - show generic message
      dashboardContent.classList.remove('hidden');
      dashboardContent.innerHTML = `
        <div class="text-center py-12">
          <h2 class="text-2xl font-semibold text-gray-800 mb-2">
            Unknown Role
          </h2>
          <p class="text-gray-600 mb-6">Role "${session.role}" is not recognized.</p>
        </div>
      `;
  }

  // Update header border color based on role
  const header = document.getElementById('app-header');
  header.className = header.className.replace(/role-\w+/g, '');
  header.classList.add(`role-${session.role}`);
}

/**
 * Handle authentication state changes for timer management.
 */
function handleAuthStateChange() {
  const { session } = getState();
  const isAuthenticated = session.authenticated;

  if (isAuthenticated && !wasAuthenticated) {
    // Just logged in - start idle timer
    startIdleTimer();
    console.log('User authenticated - idle timer started');
  } else if (!isAuthenticated && wasAuthenticated) {
    // Just logged out - stop idle timer
    stopIdleTimer();
    console.log('User logged out - idle timer stopped');
  }

  wasAuthenticated = isAuthenticated;
}

/**
 * Initialize the application.
 */
function initApp() {
  console.log('DEX UI initializing...');

  // Initialize auth area with login form
  const authArea = document.getElementById('auth-area');
  const cleanupLoginForm = initLoginForm(authArea);
  cleanupFunctions.push(cleanupLoginForm);

  // Initialize timeout warning modal
  const cleanupTimeout = initTimeoutWarning();
  cleanupFunctions.push(cleanupTimeout);

  // Initialize macro controls in header
  const macroContainer = document.getElementById('macro-controls-container');
  if (macroContainer) {
    cleanupMacroControls = initMacroControls(macroContainer);
    cleanupFunctions.push(cleanupMacroControls);
  }

  // Initialize macro preview modal
  cleanupMacroPreview = initMacroPreview();
  cleanupFunctions.push(cleanupMacroPreview);

  // Subscribe to session changes for dashboard updates
  const unsubDashboard = subscribe((state, path) => {
    if (path.includes('session') || path === '*') {
      renderDashboard();
      handleAuthStateChange();
    }
  });
  cleanupFunctions.push(unsubDashboard);

  // Try to restore previous session
  const hasSession = initSession();
  if (hasSession) {
    console.log('Previous session found - re-authenticating...');
    const { session } = getState();

    // Re-authenticate the stored key
    authenticateKey(session.apiKey)
      .then((authData) => {
        setAuthenticated({
          ...authData,
          rememberMe: session.rememberMe
        });
        console.log('Session restored successfully');
      })
      .catch((error) => {
        console.warn('Session check failed:', error.message);
        // Per user decision: expired session silently shows login dialog, no error message
        clearSession();
      });
  }

  // Initialize auth state tracking
  wasAuthenticated = getState().session.authenticated;
  if (wasAuthenticated) {
    startIdleTimer();
  }

  // Initial dashboard render
  renderDashboard();

  console.log('DEX UI initialized');
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}
